import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { CheckCircle, CreditCard } from 'lucide-react';

const StudentFeePayment = () => {
    const { currentUser } = useSelector(state => state.user);
    const [fees, setFees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [selectedFee, setSelectedFee] = useState(null);

    useEffect(() => {
        fetchFees();
    }, [fetchFees]);

    const fetchFees = useCallback(async () => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/StudentFeeList`, {
                sclassName: currentUser.sclassName._id,
                studentId: currentUser._id
            });
            setFees(res.data);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    }, [currentUser?.sclassName?._id, currentUser?._id]);

    const generatePDF = (fee, txnId) => {
        const doc = new jsPDF();
        
        // Header
        doc.setFontSize(22);
        doc.setTextColor(13, 71, 161); // IIT Patna Blue
        doc.text("INDIAN INSTITUTE OF TECHNOLOGY PATNA", 105, 20, { align: "center" });
        
        doc.setFontSize(14);
        doc.setTextColor(255, 153, 51); // Saffron
        doc.text("Official Fee Receipt", 105, 30, { align: "center" });
        
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        
        // Details
        doc.text(`Receipt No: ${txnId}`, 20, 50);
        doc.text(`Date: ${new Date().toLocaleDateString()}`, 150, 50);
        
        doc.text(`Student Name: ${currentUser.name}`, 20, 65);
        doc.text(`Roll Number: ${currentUser.rollNum}`, 20, 75);
        doc.text(`Branch ID: ${currentUser.sclassName._id.substring(0, 8)}`, 20, 85);
        
        // Table
        doc.autoTable({
            startY: 100,
            head: [['Description', 'Amount (INR)']],
            body: [
                [fee.feeType, `Rs. ${fee.amount}`],
                ['Late Fine', 'Rs. 0'],
                ['Total Paid', `Rs. ${fee.amount}`]
            ],
            theme: 'grid',
            headStyles: { fillColor: [13, 71, 161] }
        });
        
        doc.text("This is a computer-generated receipt. No signature required.", 105, 200, { align: "center" });
        doc.save(`IIT_Patna_Fee_Receipt_${txnId}.pdf`);
    };

    const handlePayment = async (fee) => {
        setProcessingId(fee._id);
        
        // Simulate Dummy Payment Gateway Delay
        setTimeout(async () => {
            try {
                const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/PaymentCreate`, {
                    student: currentUser._id,
                    fee: fee._id,
                    amountPaid: fee.amount,
                    school: currentUser.school
                });
                
                setPaymentSuccess(true);
                generatePDF(fee, res.data.transactionId);
                fetchFees(); // Refresh list
                handleCloseModal();
                
                setTimeout(() => {
                    setPaymentSuccess(false);
                    setProcessingId(null);
                }, 3000);
                
            } catch (err) {
                console.error(err);
                setProcessingId(null);
            }
        }, 2000);
    };

    const handleOpenModal = (fee) => {
        setSelectedFee(fee);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        if (processingId) return; // Don't close while processing
        setOpenModal(false);
        setSelectedFee(null);
    };

    if (loading) return <Box display="flex" justifyContent="center" mt={5}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, mt: 2 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight="800" color="#1e293b" gutterBottom>
                    Fee & Payments
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Manage your upcoming dues and view your transaction history.
                </Typography>
            </Box>
            
            {paymentSuccess && (
                <Box sx={{ bgcolor: '#dcfce7', p: 3, mb: 4, borderRadius: '16px', display: 'flex', alignItems: 'center', boxShadow: '0 4px 12px rgba(34, 197, 94, 0.1)' }}>
                    <CheckCircle color="#16a34a" style={{ marginRight: '12px' }} />
                    <Typography variant="h6" color="#166534" fontWeight="600">Payment Successful! Your official receipt is downloading.</Typography>
                </Box>
            )}

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '16px', boxShadow: '0 10px 40px -10px rgba(0,0,0,0.08)' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={{ color: '#475569', fontWeight: '700', borderBottom: '1px solid #e2e8f0' }}>Fee Type</TableCell>
                            <TableCell sx={{ color: '#475569', fontWeight: '700', borderBottom: '1px solid #e2e8f0' }}>Due Date</TableCell>
                            <TableCell sx={{ color: '#475569', fontWeight: '700', borderBottom: '1px solid #e2e8f0' }}>Amount (INR)</TableCell>
                            <TableCell sx={{ color: '#475569', fontWeight: '700', borderBottom: '1px solid #e2e8f0' }}>Status</TableCell>
                            <TableCell align="right" sx={{ color: '#475569', fontWeight: '700', borderBottom: '1px solid #e2e8f0' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fees.map((fee) => (
                            <TableRow key={fee._id} sx={{ '&:hover': { backgroundColor: '#f1f5f9' }, transition: 'background-color 0.2s' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#334155', borderBottom: '1px solid #f1f5f9' }}>{fee.feeType}</TableCell>
                                <TableCell sx={{ color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>{new Date(fee.dueDate).toLocaleDateString()}</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#0f172a', borderBottom: '1px solid #f1f5f9' }}>₹{fee.amount}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                    {fee.isPaid ? (
                                        <Box sx={{ display: 'inline-block', bgcolor: '#dcfce7', color: '#16a34a', px: 1.5, py: 0.5, borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem' }}>PAID</Box>
                                    ) : (
                                        <Box sx={{ display: 'inline-block', bgcolor: '#fee2e2', color: '#dc2626', px: 1.5, py: 0.5, borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem' }}>PENDING</Box>
                                    )}
                                </TableCell>
                                <TableCell align="right" sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                    {fee.isPaid ? (
                                        <Button variant="outlined" sx={{ color: '#16a34a', borderColor: '#16a34a', borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#dcfce7', borderColor: '#16a34a' } }} size="small" onClick={() => generatePDF(fee, fee.paymentDetails.transactionId)}>
                                            Download Receipt
                                        </Button>
                                    ) : (
                                        <Button 
                                            variant="contained" 
                                            sx={{ bgcolor: '#3b82f6', borderRadius: '10px', textTransform: 'none', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)', '&:hover': { bgcolor: '#2563eb' } }}
                                            onClick={() => handleOpenModal(fee)}
                                            disabled={processingId === fee._id}
                                            startIcon={processingId === fee._id ? <CircularProgress size={20} color="inherit" /> : <CreditCard size={18} />}
                                        >
                                            {processingId === fee._id ? 'Processing...' : 'Pay Now'}
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {fees.length === 0 && (
                    <Box p={3} textAlign="center">
                        <Typography>No fee records found for your branch.</Typography>
                    </Box>
                )}
            </TableContainer>

            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
                <DialogTitle sx={{ bgcolor: '#f8fafc', color: '#0f172a', mb: 1, fontWeight: '800' }}>Secure Payment Gateway</DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle1" sx={{ mb: 3, mt: 2, color: '#475569' }}>
                        You are paying <strong style={{ color: '#0f172a' }}>₹{selectedFee?.amount}</strong> for <strong style={{ color: '#0f172a' }}>{selectedFee?.feeType}</strong>
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Cardholder Name" variant="outlined" placeholder="John Doe" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Card Number" variant="outlined" placeholder="XXXX-XXXX-XXXX-XXXX" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Expiry Date" variant="outlined" placeholder="MM/YY" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="CVV" variant="outlined" placeholder="123" type="password" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={handleCloseModal} variant="text" sx={{ color: '#64748b', fontWeight: '600' }} disabled={processingId !== null}>Cancel</Button>
                    <Button 
                        onClick={() => handlePayment(selectedFee)} 
                        variant="contained" 
                        disabled={processingId === selectedFee?._id}
                        sx={{ minWidth: '140px', bgcolor: '#3b82f6', borderRadius: '12px', textTransform: 'none', fontSize: '1rem', '&:hover': { bgcolor: '#2563eb' } }}
                    >
                        {processingId === selectedFee?._id ? <CircularProgress size={24} color="inherit" /> : `Pay ₹${selectedFee?.amount}`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StudentFeePayment;
